import BaseModal from 'components/Modals/BaseModal';
import SearchBar from 'components/SearchBar/SearchBar';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import useWidgetSelection from './hooks/useWidgetSelection';
import { buildWidgetLookup, groupWidgetsByCategory } from './utils/groupWidgets';
import { getCardsPerRow, getVisibleWidgetIds } from './utils/layout';
import WidgetSubCategorySection from './components/WidgetSelector';

export default function WidgetSelector({
  show,
  handleShow,
  widgetData,
  userList,
  onAddWidget,
  onAddWidgets,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const scrollRegionRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(1);
  const [isCompactLayout, setIsCompactLayout] = useState(false);

  const groupedWidgets = useMemo(
    () => groupWidgetsByCategory({ userList, widgetData, searchQuery }),
    [searchQuery, userList, widgetData],
  );
  const widgetLookup = useMemo(
    () => buildWidgetLookup({ userList, widgetData }),
    [userList, widgetData],
  );
  const visibleWidgetIds = useMemo(() => getVisibleWidgetIds(groupedWidgets), [groupedWidgets]);

  const {
    expandedWidgetId,
    setExpandedWidgetId,
    selectedCount,
    isStyleSelected,
    toggleStyleSelection,
    getSelectedCountForWidget,
    pruneSelectionsByWidgetIds,
    resetSelections,
    buildSelectedWidgetConfigs,
  } = useWidgetSelection();

  useEffect(() => {
    pruneSelectionsByWidgetIds(visibleWidgetIds);
    if (expandedWidgetId !== null && !visibleWidgetIds.has(String(expandedWidgetId))) {
      setExpandedWidgetId(null);
    }
  }, [expandedWidgetId, pruneSelectionsByWidgetIds, setExpandedWidgetId, visibleWidgetIds]);

  const resetModalState = useCallback(() => {
    setSearchQuery('');
    setExpandedWidgetId(null);
    resetSelections();
  }, [resetSelections, setExpandedWidgetId]);

  useEffect(() => {
    if (!show) {
      resetModalState();
    }
  }, [show, resetModalState]);

  useEffect(() => {
    const container = scrollRegionRef.current;
    if (!container) {
      return undefined;
    }

    const updateCardsPerRow = () => {
      const containerWidth = container.clientWidth;
      setCardsPerRow(getCardsPerRow(containerWidth));
      setIsCompactLayout(containerWidth < 576);
    };

    updateCardsPerRow();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateCardsPerRow);
      return () => window.removeEventListener('resize', updateCardsPerRow);
    }

    const resizeObserver = new ResizeObserver(updateCardsPerRow);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, []);

  const closeModal = () => {
    resetModalState();
    if (typeof handleShow === 'function') {
      handleShow();
    }
  };

  const handleSave = () => {
    const selectedWidgets = buildSelectedWidgetConfigs(widgetLookup);
    if (!selectedWidgets.length) {
      return;
    }

    if (typeof onAddWidgets === 'function') {
      onAddWidgets(selectedWidgets);
    } else if (typeof onAddWidget === 'function') {
      selectedWidgets.forEach((selectedWidget) => onAddWidget(selectedWidget));
    }

    closeModal();
  };

  return (
    <BaseModal
      title="Add a new widget"
      show={show}
      handleShow={closeModal}
      backgroundColor="#325277"
      style={{ zIndex: 20000, '--bs-modal-width': 'min(94vw, 1680px)' }}
      dialogClassName="w-100 px-3"
      contentStyle={{ maxHeight: '86vh', overflow: 'hidden' }}
    >
      <div className="d-flex flex-column gap-2 text-white w-100 mx-auto" style={{ maxWidth: 1240 }}>
        <Row className="justify-content-end">
          <Col xs={12} md="auto">
            <SearchBar
              className="rounded-pill border-1 max-w-xs"
              inputClassName="text-white bg-primary-shadow rounded-pill"
              includeClear={false}
              placeholder="Search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
          </Col>
        </Row>

        <div
          ref={scrollRegionRef}
          className="customScrollbar-Y overflow-y-auto overflow-x-hidden pe-1"
          style={{ maxHeight: '72vh' }}
        >
          {groupedWidgets.length ? (
            groupedWidgets.map((category) => (
              <section key={category.category}>
                <h3 className="mb-1 fs-4">{category.category}</h3>
                {category.subItems.map((subCategory) => (
                  <WidgetSubCategorySection
                    key={`${category.category}:${subCategory.category}`}
                    categoryName={category.category}
                    subCategory={subCategory}
                    cardsPerRow={cardsPerRow}
                    expandedWidgetId={expandedWidgetId}
                    setExpandedWidgetId={setExpandedWidgetId}
                    getSelectedCountForWidget={getSelectedCountForWidget}
                    isStyleSelected={isStyleSelected}
                    toggleStyleSelection={toggleStyleSelection}
                    isCompactLayout={isCompactLayout}
                  />
                ))}
              </section>
            ))
          ) : (
            <div className="py-3 text-secondary">No widgets match your search.</div>
          )}
        </div>

        <Row className="border-top border-white border-opacity-25 pt-2 g-2 align-items-center">
          <Col xs={12} sm>
            <small className="d-block text-center text-sm-start">
              {selectedCount
                ? `${selectedCount} style${selectedCount === 1 ? '' : 's'} selected`
                : 'Select styles to save'}
            </small>
          </Col>
          <Col xs={12} sm="auto">
            <Button
              variant="dark"
              disabled={!selectedCount}
              className="fs-5 px-4 py-2 w-100"
              onClick={handleSave}
            >
              Save
            </Button>
          </Col>
        </Row>
      </div>
    </BaseModal>
  );
}
